// Lets someone refine their trip after seeing results without replaying the
// two-stage planner. Reuses the exact question components extracted in
// Phase 1 (TripPlannerQuestions.tsx) -- this file adds no second version of
// any control, only a compact collapsed-row shell around them and the
// draft-until-applied state that makes editing here safely cancelable.
//
// State model: `draft` is a local, ephemeral patch over the committed
// `preferences` prop -- never the other way around. TripPlannerWizard.tsx's
// `preferences` (and therefore the results-screen summary and the visible
// results) stay byte-identical to whatever's committed until this sheet's
// "Update My Matches" actually succeeds; dismissing the sheet any other way
// just discards `draft` on next open, per useEffect below. This is
// deliberately not a full clone of WizardPrefs -- `draft` only ever holds
// the fields the user actually touched this session, which is what makes
// "does anything differ from committed" a free comparison.
import { useEffect, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from '@/components/ui/drawer';
import { Loader2 } from 'lucide-react';
import {
  AbilityQuestion,
  SurfGoalQuestion,
  TripVibeQuestion,
  CrowdToleranceQuestion,
  WaterTempQuestion,
  MonthQuestion,
  BudgetQuestion,
} from '@/components/TripPlannerQuestions';
import {
  SURF_GOAL_OPTIONS,
  TRIP_VIBE_OPTIONS,
  CROWD_TOLERANCE_OPTIONS,
  WATER_TEMP_OPTIONS,
  monthFullName,
  optionLabel,
  capitalize,
} from '@/lib/planner-display';
import type { WizardPrefs } from '@/components/TripPlannerWizard';

type PreferenceFieldKey = keyof WizardPrefs;

export interface AdjustPreferencesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Committed preferences -- the ones that produced the results currently
   * on screen. Only read to seed each row's collapsed display and as the
   * base the draft patches over; never written to directly by this
   * component. */
  preferences: WizardPrefs;
  isSubmitting: boolean;
  errorMessage: string | null;
  onSubmit: (updated: WizardPrefs, changedFields: PreferenceFieldKey[]) => void;
}

const FIELD_LABELS: Record<PreferenceFieldKey, string> = {
  skillLevel: 'Ability',
  surfGoal: 'Surf Goal',
  tripVibe: 'Trip Vibe',
  crowdTolerance: 'Crowd Tolerance',
  month: 'Month',
  budget: 'Budget',
  waterTemp: 'Water Temperature',
};

// The one place a value gets turned into its row/summary display text --
// reads the same option arrays/labels the picker itself renders (exported
// from TripPlannerQuestions.tsx), so a collapsed row can never show a label
// that disagrees with what "Change" reveals underneath it.
const displayValueFor = (key: PreferenceFieldKey, prefs: WizardPrefs): string => {
  switch (key) {
    case 'skillLevel':
      return capitalize(prefs.skillLevel);
    case 'surfGoal':
      return optionLabel(SURF_GOAL_OPTIONS, prefs.surfGoal);
    case 'tripVibe':
      return optionLabel(TRIP_VIBE_OPTIONS, prefs.tripVibe);
    case 'crowdTolerance':
      return optionLabel(CROWD_TOLERANCE_OPTIONS, prefs.crowdTolerance);
    case 'month':
      return monthFullName(prefs.month);
    case 'budget':
      return prefs.budget;
    case 'waterTemp':
      return optionLabel(WATER_TEMP_OPTIONS, prefs.waterTemp);
  }
};

interface PreferenceRowProps {
  label: string;
  value: string;
  expanded: boolean;
  onExpand: () => void;
  children: React.ReactNode;
}

// Same collapsed-row treatment AbilityQuestion already established for its
// own signed-in summary state (TripPlannerQuestions.tsx), generalised to all
// seven fields here so the panel reads consistently regardless of which row
// a person opens. Once expanded, a row stays expanded for the rest of this
// sheet-open session -- it doesn't re-collapse after a selection, matching
// that same existing precedent rather than inventing a new one.
const PreferenceRow = ({ label, value, expanded, onExpand, children }: PreferenceRowProps) => {
  if (expanded) return <>{children}</>;
  return (
    <div className="flex items-center justify-between gap-3 p-3 sm:p-3.5 rounded-lg border border-border bg-muted/30">
      <span className="text-xs sm:text-sm font-medium">{`${label}: ${value}`}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-auto py-1 px-2 text-xs sm:text-sm shrink-0"
        onClick={onExpand}
      >
        Change
      </Button>
    </div>
  );
};

const AdjustPreferencesSheet = ({
  open,
  onOpenChange,
  preferences,
  isSubmitting,
  errorMessage,
  onSubmit,
}: AdjustPreferencesSheetProps) => {
  const isMobile = useIsMobile();
  const [draft, setDraft] = useState<Partial<WizardPrefs>>({});
  const [expanded, setExpanded] = useState<Partial<Record<PreferenceFieldKey, boolean>>>({});

  // Every open starts from a clean draft, regardless of whether the previous
  // visit applied or was dismissed -- applying already commits into
  // `preferences` (this sheet's props update accordingly) and dismissing
  // must leave no trace, so there's never a reason to carry draft state
  // across an open/close boundary either way.
  useEffect(() => {
    if (!open) return;
    setDraft({});
    setExpanded({});
  }, [open]);

  const effective: WizardPrefs = { ...preferences, ...draft };

  const changedFields = (Object.keys(draft) as PreferenceFieldKey[]).filter(
    (key) => draft[key] !== undefined && draft[key] !== preferences[key],
  );
  const isDirty = changedFields.length > 0;

  const updateDraft = <K extends PreferenceFieldKey>(key: K, value: WizardPrefs[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const expandField = (key: PreferenceFieldKey) => setExpanded((prev) => ({ ...prev, [key]: true }));

  const handleUpdateClick = () => {
    if (!isDirty || isSubmitting) return;
    onSubmit(effective, changedFields);
  };

  const formBody = (
    <div className="space-y-3 px-1 max-h-[60vh] overflow-y-auto">
      <PreferenceRow
        label={FIELD_LABELS.skillLevel}
        value={displayValueFor('skillLevel', effective)}
        expanded={!!expanded.skillLevel}
        onExpand={() => expandField('skillLevel')}
      >
        {/* showSummary is deliberately always false here: the collapsed row
            above is already this panel's one "Label: value · Change"
            moment for ability, consistent with every other field in this
            list. Re-showing AbilityQuestion's own signed-in "Your level"
            summary underneath it would just be a second, redundant collapse
            layer over the same value -- tapping "Change" here should reveal
            the full picker immediately, the same as it does for every other
            row. */}
        <AbilityQuestion
          abilityResolved
          showSummary={false}
          profileSkillLevel={null}
          onRequestChange={() => {}}
          value={effective.skillLevel}
          onChange={(v) => updateDraft('skillLevel', v)}
        />
      </PreferenceRow>

      <PreferenceRow
        label={FIELD_LABELS.surfGoal}
        value={displayValueFor('surfGoal', effective)}
        expanded={!!expanded.surfGoal}
        onExpand={() => expandField('surfGoal')}
      >
        <SurfGoalQuestion value={effective.surfGoal} onChange={(v) => updateDraft('surfGoal', v)} />
      </PreferenceRow>

      <PreferenceRow
        label={FIELD_LABELS.tripVibe}
        value={displayValueFor('tripVibe', effective)}
        expanded={!!expanded.tripVibe}
        onExpand={() => expandField('tripVibe')}
      >
        <TripVibeQuestion value={effective.tripVibe} onChange={(v) => updateDraft('tripVibe', v)} />
      </PreferenceRow>

      <PreferenceRow
        label={FIELD_LABELS.crowdTolerance}
        value={displayValueFor('crowdTolerance', effective)}
        expanded={!!expanded.crowdTolerance}
        onExpand={() => expandField('crowdTolerance')}
      >
        <CrowdToleranceQuestion
          value={effective.crowdTolerance}
          onChange={(v) => updateDraft('crowdTolerance', v)}
        />
      </PreferenceRow>

      <PreferenceRow
        label={FIELD_LABELS.month}
        value={displayValueFor('month', effective)}
        expanded={!!expanded.month}
        onExpand={() => expandField('month')}
      >
        <MonthQuestion value={effective.month} onChange={(v) => updateDraft('month', v)} />
      </PreferenceRow>

      <PreferenceRow
        label={FIELD_LABELS.budget}
        value={displayValueFor('budget', effective)}
        expanded={!!expanded.budget}
        onExpand={() => expandField('budget')}
      >
        <BudgetQuestion value={effective.budget} onChange={(v) => updateDraft('budget', v)} />
      </PreferenceRow>

      <PreferenceRow
        label={FIELD_LABELS.waterTemp}
        value={displayValueFor('waterTemp', effective)}
        expanded={!!expanded.waterTemp}
        onExpand={() => expandField('waterTemp')}
      >
        <WaterTempQuestion value={effective.waterTemp} onChange={(v) => updateDraft('waterTemp', v)} />
      </PreferenceRow>

      {errorMessage && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage}
        </p>
      )}
    </div>
  );

  const updateButton = (
    <Button onClick={handleUpdateClick} disabled={!isDirty || isSubmitting} className="gap-2">
      {isSubmitting ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Updating matches...
        </>
      ) : (
        'Update My Matches'
      )}
    </Button>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-serif">Adjust Preferences</DrawerTitle>
            <DrawerDescription>Change what matters to you, then update your matches.</DrawerDescription>
          </DrawerHeader>
          <div className="pb-2">{formBody}</div>
          <DrawerFooter>{updateButton}</DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif">Adjust Preferences</DialogTitle>
          <DialogDescription>Change what matters to you, then update your matches.</DialogDescription>
        </DialogHeader>
        {formBody}
        <DialogFooter>{updateButton}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdjustPreferencesSheet;
